<template>
  <main class="wallet-add-token-page">
    <AppNavBar backgroundColor="#fff" :title="t('wallet.addToken')" />

    <section class="tab-row">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="tab-row__item"
        :class="{ active: activeTab === tab.key }"
        @click="activeTab = tab.key"
      >
        <span>{{ tab.label }}</span>
        <i />
      </button>
    </section>

    <section v-if="activeTab === 'hot'" class="search-wrap">
      <van-field
        v-model="searchText"
        class="search-field"
        :placeholder="t('wallet.enterContractAddressSearch')"
      >
        <template #left-icon>
          <app-image
            src="@/assets/images/search_icon.png"
            alt=""
            class="search-icon"
          />
        </template>
      </van-field>
    </section>

    <section v-if="activeTab === 'hot'" class="token-list">
      <button
        v-for="item in filteredHotTokens"
        :key="item.contractAddress || item.symbol || item.coin"
        type="button"
        class="token-row"
      >
        <div class="token-row__main">
          <app-image
            v-if="item.img"
            :src="item.img"
            alt=""
            class="token-row__icon"
          />
          <div v-else class="token-row__icon token-row__icon--fallback">
            {{ tokenInitial(item) }}
          </div>

          <div class="token-row__meta">
            <div data-strong>{{ item.coin || item.symbol || "--" }}</div>
            <span>{{ simplifyAddress(item.contractAddress) }}</span>
          </div>
        </div>

        <AppSvgIcon
          v-if="isAdded(item.contractAddress)"
          name="remove_icon"
          size="24"
          color="#fff"
          class="token-row__action"
          @click.stop="removeHotToken(item)"
        />
        <AppSvgIcon
          v-else
          name="add_icon"
          size="24"
          color="var(--wp-color-brand-1)"
          class="token-row__action token-row__action--add"
          @click.stop="addHotToken(item)"
        />
      </button>

      <EmptyState v-if="!hotLoading && filteredHotTokens.length === 0" />
    </section>

    <section v-else class="custom-wrap">
      <div class="custom-card">
        <div class="field-block">
          <label>{{ t("wallet.tokenAddress") }}</label>
          <van-field
            v-model="tokenAddress"
            class="plain-field"
            :placeholder="t('wallet.enterTokenAddress')"
            @update:model-value="handleTokenAddressChange"
          />
        </div>

        <div class="field-block">
          <label>{{ t("wallet.tokenSymbol") }}</label>
          <van-field
            v-model="symbol"
            class="plain-field"
            :placeholder="t('wallet.enterTokenSymbol')"
          />
        </div>

        <div class="field-block field-block--last">
          <label>{{ t("wallet.decimalPlaces") }}</label>
          <van-field
            v-model="decimal"
            class="plain-field"
            placeholder="18"
            type="number"
          />
        </div>
      </div>

      <van-button
        block
        round
        type="primary"
        class="confirm-btn"
        :loading="customSubmitting"
        @click="submitCustomToken"
      >
        {{ t("common.confirm") }}
      </van-button>
    </section>
  </main>
</template>

<script setup>
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { showFailToast, showToast } from "vant";
import { showPassedToast } from "@/utils/toast";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { coinAddV1, coinHotList, coinInfoV1 } from "@/api/wallet";
import { useProfileStore } from "@/stores/profile";
import { useWalletTokenStore } from "@/stores/wallet-token";

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();
const walletTokenStore = useWalletTokenStore();

const activeTab = ref("hot");
const searchText = ref("");
const hotLoading = ref(false);
const hotTokens = ref([]);
const tokenAddress = ref("");
const symbol = ref("");
const decimal = ref("");
const usdtPrice = ref(0);
const customSubmitting = ref(false);
const chainNum = computed(() => profileStore.currentNetwork?.chainNum || 56);

const tabs = computed(() => [
  { key: "hot", label: t("wallet.hotToken") },
  { key: "custom", label: t("wallet.customToken") },
]);

const EmptyState = defineComponent({
  setup() {
    return () => h("div", { class: "empty-state" }, t("home.noData"));
  },
});

const filteredHotTokens = computed(() => {
  const keyword = String(searchText.value || "")
    .trim()
    .toLowerCase();
  if (!keyword) {
    return hotTokens.value;
  }
  return hotTokens.value.filter((item) =>
    JSON.stringify(item).toLowerCase().includes(keyword),
  );
});

function normalizeToken(item = {}) {
  return {
    ...item,
    coin: item?.coin || item?.symbol || "",
    symbol: item?.symbol || item?.coin || "",
    img: item?.img || item?.logo || "",
    contractAddress: item?.contractAddress || item?.address || "",
    decimalNum: Number(
      item?.decimalNum ?? item?.decimals ?? item?.decimal ?? 18,
    ),
    usdtPrice: Number(item?.usdtPrice ?? 0),
    coinType: Number(item?.coinType ?? (item?.contractAddress ? 2 : 1)),
  };
}

function simplifyAddress(value) {
  const text = String(value || "").trim();
  if (!text) {
    return "--";
  }
  if (text.length <= 12) {
    return text;
  }
  return `${text.slice(0, 6)}...${text.slice(-4)}`;
}

function tokenInitial(item) {
  return String(item?.coin || item?.symbol || "T")
    .slice(0, 1)
    .toUpperCase();
}

function isAdded(contractAddress) {
  return profileStore.hasChainToken(contractAddress, chainNum.value);
}

function buildLocalToken(item = {}) {
  return {
    contractAddress: item.contractAddress,
    coin: item.coin || item.symbol || "",
    symbol: item.symbol || item.coin || "",
    decimalNum: Number(item.decimalNum ?? item.decimals ?? item.decimal ?? 18),
    usdtPrice: Number(item.usdtPrice ?? 0),
    img: item.img || "",
    coinType: Number(item.coinType ?? 2),
  };
}

function addHotToken(item) {
  const normalized = normalizeToken(item);
  if (!normalized.contractAddress) {
    return;
  }
  walletTokenStore.upsertLocalToken(
    buildLocalToken(normalized),
    chainNum.value,
  );
  showPassedToast(t("wallet.addSuccess"));
}

function removeHotToken(item) {
  walletTokenStore.removeLocalToken(item.contractAddress, chainNum.value);
}

async function loadHotTokens() {
  hotLoading.value = true;
  try {
    const result = await coinHotList(
      { chainNum: chainNum.value },
      { meta: { showLoading: false } },
    );
    const list = Array.isArray(result)
      ? result
      : Array.isArray(result?.list)
        ? result.list
        : [];
    hotTokens.value = list
      .map(normalizeToken)
      .filter((item) => Number(item.coinType || 0) !== 1);
  } finally {
    hotLoading.value = false;
  }
}

function syncSelectedTokensFromWallet() {
  walletTokenStore.assetList.forEach((item) => {
    if (!item?.contractAddress) {
      return;
    }
    profileStore.addChainToken(buildLocalToken(item), chainNum.value);
  });
}

async function handleTokenAddressChange(value) {
  tokenAddress.value = String(value || "").trim();
  if (!/^0x[a-fA-F0-9]{40}$/.test(tokenAddress.value)) {
    return;
  }

  try {
    const result = await coinInfoV1(
      {
        contractAddress: tokenAddress.value,
        chainNum: chainNum.value,
      },
      { meta: { showLoading: false } },
    );
    symbol.value = String(result?.symbol || "");
    decimal.value = String(result?.decimals ?? "");
    usdtPrice.value = Number(result?.usdtPrice ?? 0);
  } catch (error) {
    // keep manual input available
  }
}

async function submitCustomToken() {
  if (!tokenAddress.value) {
    showFailToast(t("wallet.enterTokenAddress"));
    return;
  }
  if (isAdded(tokenAddress.value)) {
    showFailToast(t("wallet.tokenAdded"));
    return;
  }
  if (!symbol.value.trim()) {
    showFailToast(t("wallet.enterTokenSymbol"));
    return;
  }

  customSubmitting.value = true;
  try {
    await coinAddV1({
      contractAddress: tokenAddress.value.trim(),
      symbol: symbol.value.trim(),
      decimal: decimal.value.trim() || "18",
      chainNum: chainNum.value,
    });

    walletTokenStore.upsertLocalToken(
      {
        contractAddress: tokenAddress.value.trim(),
        coin: symbol.value.trim(),
        symbol: symbol.value.trim(),
        decimalNum: Number(decimal.value || 18),
        usdtPrice: usdtPrice.value,
        coinType: 2,
      },
      chainNum.value,
    );

    showPassedToast(t("wallet.addSuccess"));
    router.back();
  } finally {
    customSubmitting.value = false;
  }
}

onMounted(async () => {
  if (profileStore.platformAddress) {
    await walletTokenStore.fetchAssetList(profileStore.platformAddress, {
      meta: { showLoading: false },
    });
    syncSelectedTokensFromWallet();
  }
  await loadHotTokens();
});
</script>

<style scoped lang="scss">
.wallet-add-token-page {
  display: flex;
  flex-direction: column;
  min-height: var(--app-viewport-height);
  padding: 0 14px 24px;
  background: #ffffff;
}

.tab-row {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding-top: 20px;
}

.tab-row__item {
  padding: 0;
  border: 0;
  background: transparent;

  span {
    display: block;
    color: #666666;
    font-size: 16px;
    line-height: 22px;
  }

  i {
    display: none;
    width: 50px;
    height: 4px;
    margin-top: 10px;
    border-radius: 999px;
    background: var(--wp-color-brand-1);
    box-shadow: 0 2px 4px var(--wp-color-brand-6);
  }

  &.active {
    span {
      color: #000000;
      font-weight: 700;
    }

    i {
      display: block;
    }
  }
}

.search-wrap {
  padding-top: 20px;
  :deep(.van-cell) {
    border-radius: 12px;
  }
}

.search-field {
  :deep(.van-cell) {
    border-radius: 12px;
    background: #f4f5f6;
    padding: 10px 16px;
  }

  :deep(.van-field__left-icon) {
    margin-right: 6px;
    margin-top: 3px;
  }
}

.search-icon {
  width: 17px;
  height: 17px;
  object-fit: contain;
}

.token-list {
  flex: 1;
  padding-top: 10px;
}

.token-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 60px;
  padding: 10px 0;
  border: 0;
  background: transparent;
}

.token-row__main {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
}

.token-row__icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.token-row__icon--fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--wp-color-theme);
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
}

.token-row__meta {
  min-width: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  [data-strong],
  span {
    display: block;
  }

  [data-strong] {
    color: #1d2129;
    font-size: 15px;
    line-height: 21px;
    font-weight: 500;
  }

  span {
    padding-top: 2px;
    color: #86909c;
    font-size: 12px;
    line-height: 18px;
  }
}

.token-row__action {
  width: 24px;
  height: 24px;
  object-fit: contain;
  flex-shrink: 0;
}

.token-row__action--add {
}

.custom-wrap {
  padding-top: 20px;
}

.custom-card {
  padding: 15px;
  border-radius: 16px;
  background: #f7f7f7;
}

.field-block {
  padding-bottom: 20px;

  label {
    display: block;
    padding-bottom: 10px;
    color: #333333;
    font-size: 14px;
    line-height: 20px;
  }
  ::deep(.van-field) {
    background: #fff !important;
  }
}

.field-block--last {
  padding-bottom: 0;
}

.plain-field {
  background: #fff;
  border-radius: 12px;
  :deep(.van-cell) {
    border-radius: 12px;
    background: #ffffff;
    padding: 12px 14px;
  }
}

.confirm-btn {
  margin-top: 30px;
}

.empty-state {
  padding-top: 48px;
  color: #999999;
  font-size: 14px;
  text-align: center;
}
</style>
